/**
 * /install — public, so the marketing site can link to it and it opens before
 * anyone has an account. What it shows depends on the device: on a phone, the
 * browser's own install dialog where there is one and the steps by hand where
 * there is not; on a computer, a bookmark and a desktop shortcut. The site's
 * Android, iPhone and Computer buttons add `?for=…`; picked on a different
 * device, the page says where to open it.
 *
 * A home-screen icon belongs to the origin that installs it, which is why this
 * page lives in the app and not on the-inner-awakening.com.
 */
import { useState } from 'react'

import { InstallPanel } from '../components/install/InstallPanel'
import { bookmarkKeys, deviceTarget, installMode } from '../lib/install/device'
import {
  useChosenInstallTarget,
  useDeviceReading,
  useInstallAddress,
  useInstallPromptState,
} from '../lib/install/hooks'
import { promptInstall } from '../lib/install/prompt-store'

export default function Install() {
  const device = useDeviceReading()
  const prompt = useInstallPromptState()
  const chosen = useChosenInstallTarget()
  const installAddress = useInstallAddress()
  const [isDeclined, setIsDeclined] = useState(false)

  // Once the dialog has been closed the captured event is spent, so the mode
  // would fall back to 'unsupported'; the page keeps saying what just happened.
  const detected = installMode({ ...device, hasInstallPrompt: prompt.event !== null })
  const mode = isDeclined ? 'prompt' : detected
  const here = deviceTarget(device)
  const target = chosen ?? here
  // Already installed on this device wins: the app is the place to be.
  const isOtherDevice = chosen !== null && chosen !== here && mode !== 'installed'

  return (
    <div
      className="min-h-svh"
      style={{ background: 'radial-gradient(ellipse at 60% 0%, rgba(155,114,192,0.1) 0%, transparent 50%), #FAF7F4' }}
    >
      <main className="mx-auto max-w-xl px-6 py-12 page-enter">
        <InstallPanel
          mode={mode}
          target={target}
          isOtherDevice={isOtherDevice}
          installAddress={installAddress}
          isJustInstalled={prompt.isInstalled}
          isDeclined={isDeclined}
          onInstall={() => {
            setIsDeclined(false)
            void promptInstall().then((outcome) => {
              if (outcome === 'dismissed') setIsDeclined(true)
            })
          }}
          bookmarkKeys={bookmarkKeys(device)}
        />
      </main>
    </div>
  )
}
